'use client';

import { useTranslation } from 'next-i18next';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import {
  VideoCameraIcon,
  PhoneIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/solid';
import {
  Button,
  Dropdown,
  DropdownButton,
  DropdownItems,
  DropdownItem,
} from '@components/common';
import { cn } from '@root/src/utils/cn';
import { useDropdownHover } from '@hooks/common/useDropdownHover';
import useWindowDimensions from '@hooks/useMediaQuery';

interface InterviewResponseButtonProps {
  jobSlug: string;
  className?: string;
}

export const InterviewResponseButton = (props: InterviewResponseButtonProps) => {
  const { jobSlug, className } = props;
  const { t } = useTranslation('common');
  const { router } = useExtendedRouter();
  const { isTablet } = useWindowDimensions();

  // Enable hover behavior only on desktop
  const { dropdownRef, buttonRef, closeFuncRef } = useDropdownHover({ enabled: !isTablet });

  const handleInterviewTypeClick = (type: 'video' | 'audio' | 'text') => {
    switch (type) {
      case 'video':
        void router.push(`/job/${jobSlug}/ai-interview?type=video`);
        break;
      case 'audio':
        void router.push(`/job/${jobSlug}/ai-interview?type=audio`);
        break;
      case 'text':
        void router.push(`/job/${jobSlug}/ai-interview?type=text`);
        break;
      default:
        break;
    }
  };

  return (
    <Dropdown ref={dropdownRef} className="relative flex w-full">
      {({ close }) => {
        closeFuncRef.current = close;

        return (
          <>
            <DropdownButton
              as={Button}
              color="primary-light"
              rounding="normal"
              styling="soft"
              size="xs"
              className={cn('group/dropdownButton flex rounded-2xl flex-1 items-center justify-center gap-2 overflow-hidden !bg-primary/20 !text-xs !text-primary-light hover:!bg-primary-dark hover:!text-white', className)}
              ref={buttonRef}
            >
              <span>{t('homepage.chip.start_interview')}</span>
              <div className="flex items-center divide-x divide-primary-light transition-colors duration-200 group-hover/dropdownButton:divide-white">
                {/*<div className="px-2">
                  <VideoCameraIcon className="size-4" />
                </div>
                <div className="px-2">
                  <PhoneIcon className="size-4" />
                </div>
                <div className="px-2">
                  <PencilSquareIcon className="size-4" />
                </div>*/}
              </div>
            </DropdownButton>

            <DropdownItems
              animation="slideUp"
              className="bottom-full flex w-64 flex-col gap-2 rounded-md border border-surface-200 bg-surface p-1 shadow-lg"
            >
              {/* Video Interview Option */}
              <DropdownItem
                as={Button}
                type="button"
                styling="none"
                onClick={() => handleInterviewTypeClick('video')}
                className="flex items-center gap-3 rounded-md px-2 py-1 text-left text-sm transition-colors hover:bg-surface-100"
              >
                <VideoCameraIcon className="size-5" />
                <span className="font-medium text-ink">{t('ai.video.interview')}</span>
              </DropdownItem>

              {/* Audio Interview Option */}
              <DropdownItem
                as={Button}
                type="button"
                styling="none"
                onClick={() => handleInterviewTypeClick('audio')}
                className="flex items-center gap-3 rounded-md px-2 py-1 text-left text-sm transition-colors hover:bg-surface-100"
              >
                <PhoneIcon className="size-5" />
                <span className="font-medium text-ink">{t('ai.audio.interview')}</span>
              </DropdownItem>

              {/* Text Interview Option */}
              <DropdownItem
                as={Button}
                type="button"
                styling="none"
                onClick={() => handleInterviewTypeClick('text')}
                className="flex items-center gap-3 rounded-md px-2 py-1 text-left text-sm transition-colors hover:bg-surface-100"
              >
                <PencilSquareIcon className="size-5" />
                <span className="font-medium text-ink">{t('ai.text.interview')}</span>
              </DropdownItem>
            </DropdownItems>
          </>
        );
      }}
    </Dropdown>
  );
};
